import { useCallback, useEffect, useState } from "react";
import { Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const ROUTES = ["appointed", "co-opted", "casual vacancy", "ex officio"];

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const PostHistory = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/office-holders");
    setData(res.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/office-holders", form);
      toast.success("The appointment is on the register");
      await load();
      setForm(null);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!data) return <Panel title="Who held which post" testId="panel-holders-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening the register…</p></Panel>;

  return (
    <div className="space-y-4">
      <Panel title="Who held which post" testId="panel-holders"
             kicker={`${data.total} term(s) on the register, taken straight off the certified results`}
             actions={data.may_record && (
               <Button size="sm" className="h-8 rounded-full" data-testid="record-appointment"
                       onClick={() => setForm({ name: "", email: "", post_title: "",
                         term_start: "", term_end: "", route: "appointed", minute: "" })}>
                 <UserCheck className="mr-1.5 h-3.5 w-3.5" /> Record an appointment
               </Button>
             )}>
        {data.posts.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="holders-empty">
            No certified result has filled a post yet.
          </p>
        ) : (
          <div className="space-y-4">
            {data.posts.map((post) => (
              <div key={post.post_title} data-testid={`holders-post-${slug(post.post_title)}`}>
                <p className="kicker">{post.post_title}</p>
                <Table head={(
                  <>
                    <Th>Holder</Th><Th>Term</Th><Th>Route</Th><Th>In a row</Th><Th>Authority</Th>
                  </>
                )}>
                  {post.terms.map((t) => (
                    <tr key={`${t.name}-${t.term_start}`}
                        data-testid={`holder-${slug(t.name)}-${t.term_start}`}>
                      <Td className="font-medium text-foreground">{t.name}</Td>
                      <Td>{t.term_start} to {t.term_end}</Td>
                      <Td>
                        <Chip className={t.route.startsWith("elected")
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "border-primary/30 bg-primary/10 text-primary"}>
                          {t.route}
                        </Chip>
                      </Td>
                      <Td>
                        <span className={t.consecutive_terms > 2
                          ? "font-mono text-amber-600 dark:text-amber-400" : "font-mono"}>
                          {t.consecutive_terms}
                        </span>
                      </Td>
                      <Td className="text-[11px]">
                        {t.results_ref || t.appointment_ref}
                        {t.minute ? ` · ${t.minute}` : ""}
                      </Td>
                    </tr>
                  ))}
                </Table>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="appointment-dialog">
          <DialogHeader><DialogTitle>Record an appointment</DialogTitle>
            <DialogDescription>
              For a co-option, a casual vacancy or an ex officio seat. It sits on the same register
              as the elected terms, with the route shown plainly.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Name">
              <Input data-testid="appointment-name" value={form?.name || ""}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </F>
            <F label="Email">
              <Input data-testid="appointment-email" value={form?.email || ""}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </F>
            <F label="Post">
              <Input data-testid="appointment-post" value={form?.post_title || ""}
                     onChange={(e) => setForm({ ...form, post_title: e.target.value })} />
            </F>
            <F label="Route">
              <Select value={form?.route}
                      onValueChange={(v) => setForm({ ...form, route: v })}>
                <SelectTrigger data-testid="appointment-route"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROUTES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Term starts">
              <Input type="date" data-testid="appointment-start" value={form?.term_start || ""}
                     onChange={(e) => setForm({ ...form, term_start: e.target.value })} />
            </F>
            <F label="Term ends">
              <Input type="date" data-testid="appointment-end" value={form?.term_end || ""}
                     onChange={(e) => setForm({ ...form, term_end: e.target.value })} />
            </F>
          </div>
          <F label="Minute">
            <Textarea rows={3} data-testid="appointment-minute" value={form?.minute || ""}
                      onChange={(e) => setForm({ ...form, minute: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="appointment-save" disabled={busy} onClick={save}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
